import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save, X } from 'lucide-react';
import { Headshot } from './Headshot';
import { SLOT_NAMES } from './ticketDate';
import { InjuryTag } from './InjuryTag';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting`;
const CARDS = {
  nba: [['points', 'Points'], ['rebounds', 'Rebounds'], ['assists', 'Assists'], ['threes', '3-pointers'], ['steals', 'Steals'], ['blocks', 'Blocks'], ['stocks', 'Steals + blocks'], ['dd', 'Double-doubles']],
  nfl: [['passing_yards', 'Passing yards'], ['passing_tds', 'Passing TDs'], ['rushing_yards', 'Rushing yards'], ['receiving_yards', 'Receiving yards'], ['anytime_td', 'Touchdowns']],
};
const pct = (p) => `${Math.round((p || 0) * 100)}%`;
const day = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric', year: '2-digit' }) : '');
const lineText = (cat, unit, m) => (cat === 'dd' ? 'double-double' : cat === 'anytime_td' ? 'anytime touchdown' : `${m.mark}+ ${unit}`);
const val = (cat, v) => (v == null ? '—' : cat === 'dd' ? (v ? 'DD ✓' : 'no DD') : cat === 'anytime_td' ? `${v} TD` : v);

const toLeg = (league, cat, unit, l, mode) => {
  const m = l[mode];
  return {
    league, bet_type: 'player_prop', player_name: l.player, selection: `${l.player} ${lineText(cat, unit, m)}`,
    line: cat === 'dd' ? 2 : m.mark, stat_target: cat, game_date: l.date, espn_event_id: l.event_id,
    away_team: l.away_team, home_team: l.home_team, athlete_id: l.player_id, team_logo: l.team_logo,
  };
};

const Line = ({ tag, m, on, cat, unit, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`rounded px-1.5 py-0.5 text-[10px] font-black transition-colors ${on ? 'bg-emerald-500 text-black' : 'border border-zinc-700 text-zinc-300 hover:bg-zinc-800'}`}>
    {tag} {cat === 'dd' || cat === 'anytime_td' ? '' : `${m.mark}+`} · {pct(m.prob)}
  </button>
);

const PlayerRow = ({ l, i, cat, unit, league, mode, onMode, onRemove, tid }) => (
  <li className="flex items-start gap-2 py-1.5" data-testid={`cat-leg-${tid}-${i}`}>
    <span className="w-4 shrink-0 pt-1 text-right text-[11px] font-bold text-zinc-500">{i + 1}.</span>
    {league === 'nba' ? <Headshot leg={{ ...l, league, athlete_id: l.player_id, player_name: l.player }} size={30} testId={`cat-face-${tid}-${i}`} badge={false} />
      : l.team_logo && <img src={l.team_logo} alt="" className="h-7 w-7 shrink-0 object-contain" />}
    <div className="min-w-0 flex-1">
      <p className="text-[13px] font-bold text-white">
        {l.player} <span className="text-[10px] font-semibold text-zinc-400">{l.team} vs {l.opponent}{l.role ? ` · ${l.role}` : ''}</span>
        {l.questionable && <span className="ml-1"><InjuryTag injury={{ status: l.questionable }} testId={`cat-leg-injury-${l.player_id}`} /></span>}
      </p>
      <div className="mt-0.5 flex flex-wrap gap-1">
        {cat !== 'dd' && cat !== 'anytime_td' && <Line tag="SMART" m={l.smart} on={mode === 'smart'} cat={cat} unit={unit} onClick={() => onMode('smart')} testId={`cat-smart-${tid}-${i}`} />}
        <Line tag="SAFE" m={l.safe} on={mode === 'safe'} cat={cat} unit={unit} onClick={() => onMode('safe')} testId={`cat-safe-${tid}-${i}`} />
        {cat !== 'dd' && cat !== 'anytime_td' && <Line tag="AVG" m={l.avg} on={mode === 'avg'} cat={cat} unit={unit} onClick={() => onMode('avg')} testId={`cat-avg-${tid}-${i}`} />}
      </div>
      <p className="mt-0.5 text-[10px] leading-4 text-zinc-400" data-testid={`cat-stats-${tid}-${i}`}>
        <b className="text-zinc-200">Avg {cat === 'dd' ? `DD ${pct(l.season_avg)} of games` : l.season_avg}</b>
        {' · '}Last game: <b className="text-zinc-200">{l.last_game ? `${val(cat, l.last_game.value)} vs ${l.last_game.opp} ${day(l.last_game.date)}` : '—'}</b>
        {' · '}Last vs {l.opponent}: <b className="text-sky-300">{l.vs_opp ? `${val(cat, l.vs_opp.value)} (${day(l.vs_opp.date)})` : "hasn't played them"}</b>
      </p>
      {l.opp_note && <p className="text-[10px] text-zinc-500">{l.opp_note}</p>}
      <p className="text-[9px] text-zinc-600">{l.game}{l.date ? ` · ${new Date(l.date).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short', hour: 'numeric', minute: '2-digit' })} PT` : ''}</p>
    </div>
    <button type="button" onClick={onRemove} className="rounded p-1 text-zinc-500 hover:bg-zinc-800 hover:text-red-300" data-testid={`cat-remove-${tid}-${i}`} aria-label="Remove player"><X className="h-3.5 w-3.5" /></button>
  </li>
);

const CatTicket = ({ t, tid, res, league }) => {
  const [legs, setLegs] = useState(t.legs);
  const [modes, setModes] = useState(() => t.legs.map(() => 'smart'));
  const [stake, setStake] = useState(t.stake);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const all = (m) => setModes(legs.map(() => m));
  const chance = legs.reduce((p, l, i) => p * (l[modes[i]]?.prob || 0), 1);
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/parlays`, { platform: 'fanduel', stake, potential_payout: 0, source: 'category_card',
        legs: legs.map((l, i) => toLeg(league, res.category, res.unit, l, modes[i])) });
      setSaved(true);
      toast.success('Saved to your board — add the payout with the pencil once you place it');
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSaving(false);
  };
  return (
    <div className={`rounded-xl border p-3 ${t.leftover ? 'border-zinc-700 bg-zinc-950/60' : 'border-emerald-500/50 bg-emerald-950/15'}`} data-testid={`cat-ticket-${tid}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="mr-auto text-sm font-black text-white">{t.title} · {legs.length} legs <span className="text-[11px] font-semibold text-zinc-400">all hit <b className="text-amber-300" data-testid={`cat-chance-${tid}`}>{(chance * 100).toFixed(1)}%</b></span></p>
        {res.category !== 'dd' && res.category !== 'anytime_td' && (
          <button type="button" onClick={() => all('smart')} className="rounded border border-emerald-600 px-1.5 py-0.5 text-[10px] font-bold text-emerald-300 hover:bg-emerald-900/40" data-testid={`cat-all-smart-${tid}`}>All smart</button>
        )}
        <button type="button" onClick={() => all('safe')} className="rounded border border-zinc-700 px-1.5 py-0.5 text-[10px] font-bold text-zinc-300 hover:bg-zinc-800" data-testid={`cat-all-safe-${tid}`}>All safe</button>
        {res.category !== 'dd' && res.category !== 'anytime_td' && (
          <button type="button" onClick={() => all('avg')} className="rounded border border-zinc-700 px-1.5 py-0.5 text-[10px] font-bold text-zinc-300 hover:bg-zinc-800" data-testid={`cat-all-avg-${tid}`}>All at average</button>
        )}
        <input type="number" min="1" value={stake} onChange={(e) => setStake(Number(e.target.value) || 0)} className="w-14 rounded border border-zinc-700 bg-zinc-950 px-1 py-0.5 text-[11px] text-white" data-testid={`cat-stake-${tid}`} />
        <button type="button" onClick={save} disabled={saving || saved || !legs.length || stake <= 0} data-testid={`cat-save-${tid}`}
          className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-2 py-1 text-[11px] font-black text-black hover:bg-emerald-400 disabled:opacity-50">
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}{saved ? 'Saved' : 'Save'}
        </button>
      </div>
      <ol className="mt-1 divide-y divide-zinc-800/80">
        {legs.map((l, i) => (
          <PlayerRow key={l.player_id} l={l} i={i} tid={tid} cat={res.category} unit={res.unit} league={league} mode={modes[i]}
            onMode={(m) => setModes(modes.map((x, j) => (j === i ? m : x)))}
            onRemove={() => { setLegs(legs.filter((_, j) => j !== i)); setModes(modes.filter((_, j) => j !== i)); }} />
        ))}
      </ol>
    </div>
  );
};

// Tap a stat card → tickets of 10 from today's games, each player checked against tonight's opponent.
export const CategoryCards = ({ league, stake }) => {
  const [busy, setBusy] = useState('');
  const [res, setRes] = useState(null);
  const run = async (cat) => {
    setBusy(cat);
    try {
      const r = (await axios.get(`${API}/category-cards/${league}/${cat}`, { params: { stake } })).data;
      setRes(r);
      if (!r.tickets.length) toast.info(r.note || 'No players for this card right now');
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy('');
  };
  return (
    <div className="space-y-2" data-testid={`category-cards-${league}`}>
      <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-400">Build by stat — 10 players per ticket</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {CARDS[league].map(([k, label]) => (
          <button key={k} type="button" onClick={() => run(k)} disabled={!!busy} data-testid={`category-card-${league}-${k}`}
            className={`flex min-h-[52px] items-center justify-center gap-1.5 rounded-xl border px-2 py-3 text-sm font-black transition-transform active:scale-95 disabled:opacity-60 ${res?.category === k ? 'border-emerald-400 bg-emerald-500/15 text-emerald-200' : 'border-zinc-700 bg-zinc-900 text-white hover:border-zinc-500'}`}>
            {busy === k && <Loader2 className="h-3.5 w-3.5 animate-spin" />}{label}
          </button>
        ))}
      </div>
      {res && (
        <div className="space-y-2" data-testid="category-results">
          <p className="text-[11px] text-zinc-400" data-testid="category-summary">
            {res.label} · {res.slate} · {res.players} players{res.windows?.length ? ` (${res.windows.map((w) => `${w.label.split(' · ')[0]} ${w.players}`).join(', ')})` : ''} → {res.tickets.filter((t) => !t.leftover).length} ticket{res.tickets.filter((t) => !t.leftover).length === 1 ? '' : 's'} of 10{res.tickets.some((t) => t.leftover) ? ' + leftovers' : ''}. SMART (picked) = what he should do vs this opponent, ~7 in 10 hit · SAFE = below his usual · AVG = right at his average. Tap a line to switch it, X to drop a player.
          </p>
          {res.tickets.map((t, i) => (
            <div key={`${res.category}-${t.title}`} className="space-y-2">
              {t.window && t.window !== res.tickets[i - 1]?.window && (
                <p className="pt-1 text-xs font-bold uppercase tracking-wide text-amber-300" data-testid={`cat-window-${t.window}`}>{SLOT_NAMES[t.window]}</p>
              )}
              <CatTicket t={t} tid={i} res={res} league={league} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
